import React, { useContext } from 'react';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { UserContext } from '../../context/UserContext';
import { useDesktopMedia } from '../../util/MediaQuery';
import { isGuidedOnboardingEnabled } from '../../util/guidedOnboarding';
import colors from '../../styles/colors';
import {
  getVisibleChecklistSteps,
  isStepComplete,
} from './checklistConfig';

const LEFT_RAIL_PX = 80;

const Widget = styled.button`
  position: fixed;
  left: ${LEFT_RAIL_PX + 8}px;
  bottom: 88px;
  z-index: 860;
  width: min(280px, calc(100vw - ${LEFT_RAIL_PX + 24}px));
  margin: 0;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  border: 1px solid ${colors.grey5};
  border-radius: 10px;
  background: ${colors.white};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-family: inherit;
  &:hover {
    border-color: ${colors.seaGreen};
    box-shadow: 0 8px 28px rgba(39, 187, 173, 0.2);
  }
`;

const WidgetTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.grey2};
  margin-bottom: 10px;
`;

const ProgressTrack = styled.div`
  display: flex;
  gap: 5px;
`;

const ProgressDash = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: ${(p) => (p.$complete ? colors.seaGreen : colors.grey5)};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${colors.grey3};
  margin-top: 8px;
  line-height: 1.35;
`;

/**
 * D-29: after completing a checklist step 4–7, prompt user to reopen the checklist (compass).
 */
export default function ContinueSetupWidget() {
  const { app, setApp } = useContext(AppContext);
  const { user } = useContext(UserContext);
  const isDesktop = useDesktopMedia();
  const afterStepId = app?.onboarding?.continueWidgetAfterStepId;
  const org = user?.organization;
  const progress = org?.onboardingChecklistProgress;
  const visibleSteps = getVisibleChecklistSteps(org);

  const openChecklist = () => {
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        checklistSidebarOpen: true,
        checklistDeferTourUntilStepClick: true,
        focusedChecklistStepId: null,
        tourScreenIndex: 0,
        continueWidgetAfterStepId: null,
      },
    }));
  };

  if (
    !isGuidedOnboardingEnabled(user)
    || !isDesktop
    || afterStepId == null
    || !progress
  ) {
    return null;
  }

  return (
    <Widget
      type="button"
      data-help="onboarding-continue-widget"
      onClick={openChecklist}
      aria-label="Continue account setup — open checklist"
    >
      <WidgetTitle>Continue Your Account Setup</WidgetTitle>
      <ProgressTrack aria-hidden>
        {visibleSteps.map((s) => (
          <ProgressDash
            key={s.id}
            $complete={isStepComplete(s.id, progress)}
          />
        ))}
      </ProgressTrack>
      <Hint>Click to open the checklist, or use the compass in the sidebar.</Hint>
    </Widget>
  );
}
